import React, { useMemo } from "react";
import { AbsoluteFill, Sequence, useVideoConfig, staticFile } from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { pullOut } from "../Transition/PullOut";
import { date, z } from "zod";
import ImageFrame from "../Overlay/lndbnk/ImageFrame";
import Footer from "../Overlay/lndbnk/Footer";
import MiniImage from "../Overlay/lndbnk/MiniImage";
import FullscreenImage from "../Compostion/FullscreenImage";
import SlidingReveal from "../Overlay/lndbnk/SlidingReveal";
import ParagraphDisplay from "../Overlay/lndbnk/ParagraphDisplay";
import Overlay from "../Overlay/Overlay";



// Zod schema for LandingPageComposition
const LandingPageCompositionPropsSchema = z.object({
  backgroundColor: z.string().default("#011226").describe("Background color"),
  mainSrc: z.string().default("").describe("Image URL for ImageFrame"),
  img01src: z.string().default("").describe("Image URL for Mini Image 1"),
  img02src: z.string().default("").describe("Image URL for Mini Image 2"),
  img03src: z.string().default("").describe("Image URL for Mini Image 3"),
  footerBackgroundColor: z.string().default("#ffffff").describe("Footer: Background color"),
  headerText: z.string().default("LIVE AUCTION").describe("Header text at top"),
  footerText: z.string().default("INVESTMENT OPPORTUNITY").describe("Footer text at bottom"),
  revealText: z.string().default("OPEN HOUSE EVENT").describe("Text for sliding reveal"),
  paragraphText: z.string().default("Join us for an exclusive open house event showcasing this stunning property. Explore the elegant interiors, modern amenities, and beautiful surroundings. Don't miss this opportunity to find your dream home!").describe("Paragraph text for display"),
  addressText: z.string().default("123 Anywhere St, Somewhere City, 12345").describe("Address text"),
  dateText: z.string().default("October 15, 2025").describe("Date text"),
  timeText: z.string().default("10:00 AM - 12:00 PM").describe("Time text"),
  qrDeeplink: z.string().default("http://ldbnk.app").describe("QR code deeplink URL"),
});

export { LandingPageCompositionPropsSchema };

const LandingPageComposition = ({
  // largeImage Story props
  mainSrc = "https://s3.us-east-2.amazonaws.com/dlba-production-bucket/property_images/9446393/IMG_20250924_134047.jpg",
  largeImageStartFrame = 10,
  largeImageEndFrame = 162,
  largeImageInAnimation = "fade",
  largeImageOutAnimation = "slideTop",

  // Footer props
  footerBackgroundColor = "#ffffff",
  footerStartFrame = 50,
  footerEndFrame = 162,
  footerInAnimation = "none",
  footerOutAnimation = "slideBottom",

  // Mini Image props
  img01src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&h=1080&fit=crop",
  img02src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&h=1080&fit=crop",
  img03src = "https://images.unsplash.com/photo-1506744038136-46273834b3fb?w=1920&h=1080&fit=crop",
  miniImageStartFrame = 60,
  miniImageEndFrame = 165,
  miniImageInAnimation = "slideBottom",
  miniImageOutAnimation = "slideBottom",

  image2StartFrame = 80,
  image3StartFrame = 100,
  headerText = "LIVE AUCTION",
  footerText = "INVESTMENT OPPORTUNITY",
  revealText = "OPEN HOUSE EVENT",
  paragraphText = "Join us for an exclusive open house event showcasing this stunning property. Explore the elegant interiors, modern amenities, and beautiful surroundings. Don't miss this opportunity to find your dream home!",
  addressText = "123 Anywhere St, Somewhere City, 12345",
  dateText = "October 15, 2025",
  timeText = "10:00 AM - 12:00 PM",
  qrDeeplink = "http://ldbnk.app",
  // General props
  videoDuration = 540,
  backgroundColor = "#011226",
}) => {
  const { durationInFrames } = useVideoConfig();

  // Use provided duration or fallback to video config
  const finalDuration = useMemo(() => {
    return videoDuration || durationInFrames ;
  }, [durationInFrames, videoDuration]);

  return (
    <AbsoluteFill
      style={{
        backgroundColor,
      }}
    >
      <TransitionSeries>
        <TransitionSeries.Sequence durationInFrames={182}>
          <AbsoluteFill
            style={{
              backgroundColor,
              zIndex: -1,
            }}
          />
          {/* Info Layer - Address, Date, Time */}
          <div
            style={{
              width: "714px",
              height: "210px",
              transform: "rotate(-10deg)",
              flexShrink: 0,
              position: "absolute",
              left: "168px",
              top: "910px",
              zIndex: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {/* Open House Title */}
            <div
              style={{
                position: "relative",
                zIndex: 0,
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "700",
                  fontSize: "90px",
                  color: "rgba(255,255,255,1)",
                }}
              >
                {headerText.toUpperCase()}
              </span>
            </div>

            {/* Address */}
            <div
              style={{
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "36px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
                marginBottom: "25px",
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "400",
                  fontSize: "36px",
                  color: "rgba(255,255,255,1)",
                }}
              >
                {addressText}
              </span>
            </div>

            {/* Date */}
            <div
              style={{
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "52px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "400",
                  fontSize: "52px",
                  color: "rgba(255,255,255,1)",
                }}
              >
                {dateText}
              </span>
            </div>

            {/* Time */}
            <div
              style={{
                color: "#FFF",
                textAlign: "center",
                fontFamily: "Inter",
                fontSize: "32px",
                fontStyle: "normal",
                fontWeight: "400",
                lineHeight: "normal",
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "400",
                  fontSize: "32px",
                  color: "rgba(255,255,255,1)",
                }}
              >
                {timeText}
              </span>
            </div>
          </div>
          {/* ImageFrame overlay */}
          <ImageFrame
            imageSrc={mainSrc}
            startFrame={largeImageStartFrame}
            endFrame={Math.min(largeImageEndFrame, finalDuration)}
            inAnimation={largeImageInAnimation}
            outAnimation={largeImageOutAnimation}
            inDuration={20}
            outDuration={15}
            x={0}
            y={0}
            opacity={1}
            zIndex={0}
          />

          {/* Footer overlay */}
          <Footer
            backgroundColor={footerBackgroundColor}
            startFrame={footerStartFrame}
            endFrame={Math.min(footerEndFrame, finalDuration)}
            inAnimation={footerInAnimation}
            outAnimation={footerOutAnimation}
            inDuration={20}
            outDuration={15}
            x={0}
            y={0}
            opacity={1}
            zIndex={0}
          />

          {/* Mini Image overlay */}
          <MiniImage
            imageSrc={img01src}
            startFrame={miniImageStartFrame}
            endFrame={Math.min(miniImageEndFrame, finalDuration)}
            inAnimation={miniImageInAnimation}
            outAnimation={miniImageOutAnimation}
            inDuration={20}
            outDuration={15}
            x={0}
            y={0}
            opacity={1}
            zIndex={0}
          />

          {/* Mini Image overlay */}
          <MiniImage
            imageSrc={img02src}
            startFrame={image2StartFrame}
            endFrame={Math.min(miniImageEndFrame, finalDuration)}
            inAnimation={miniImageInAnimation}
            outAnimation={miniImageOutAnimation}
            inDuration={20}
            outDuration={15}
            x={367}
            y={-64}
            opacity={1}
            zIndex={0}
          />

          {/* Mini Image overlay */}
          <MiniImage
            imageSrc={img03src}
            startFrame={image3StartFrame}
            endFrame={Math.min(miniImageEndFrame, finalDuration)}
            inAnimation={miniImageInAnimation}
            outAnimation={miniImageOutAnimation}
            inDuration={20}
            outDuration={15}
            x={736}
            y={-128} // 64 + 64
            opacity={1}
            zIndex={0}
          />
          {/* ldbnk.app text - top right corner */}
          <Overlay
            startFrame={20}
            endFrame={160}
            inAnimation={"slideTop"}
            outAnimation={"fade"}
          >
            <div
              style={{
                position: "absolute",
                top: "10px",
                left: "40px",
                zIndex: 10,
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "700",
                  fontSize: "40px",
                  color: "rgba(255, 255, 255, 1)",
                }}
              >
                ldbnk.app
              </span>
            </div>
          </Overlay>

        {/* Icon - bottom left corner */}
          <Overlay
            startFrame={75}
            endFrame={160}
            inAnimation={"slideBottom"}
            outAnimation={"fade"}
            x={20}
            y={1920 - 150 - 40}
            zIndex={10}
          >
            <img
              src={staticFile("youtube-downloads/icon.png")}
              alt="Icon"
              style={{
                width: "150px",
                height: "150px",
                display: "block",
              }}
            />
          </Overlay>

          {/* Investment Opp - bottom left text */}
          <Overlay
            startFrame={90}
            endFrame={160}
            inAnimation={"slideBottom"}
            outAnimation={"fade"}
          >
            <div
              style={{
                position: "relative",
                top: "1675px",
                left: "110px",
                transform: "rotate(-10deg)",
                zIndex: 1,
              }}
            >
              <span
                style={{
                  fontFamily:
                    "Inter, -apple-system, Roboto, Helvetica, sans-serif",
                  fontWeight: "700",
                  fontSize: "80px",
                  color: "rgba(0, 0, 0, 1)",
                  textAlign: 'center',
                  display: 'block',
                  width: '714px',
                }}
              >
                {footerText.toUpperCase()}
              </span>
            </div>
          </Overlay>

          {/* QR Code - bottom right corner */}
          <Overlay
            startFrame={50}
            endFrame={160}
            inAnimation={"slideBottom"}
            outAnimation={"fade"}
            x={1080 - 250 - 40}
            y={1920 - 250 - 40}
            zIndex={10}
          >
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${qrDeeplink}`}
              alt="ldbnk.app QR Code"
              style={{
                width: "250px",
                height: "250px",
                display: "block",
              }}
            />
          </Overlay>
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={pullOut()}
          timing={linearTiming({ durationInFrames: 30 })}
        />

        <TransitionSeries.Sequence durationInFrames={150}>
          <FullscreenImage imageSrc={img01src} />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={pullOut()}
          timing={linearTiming({ durationInFrames: 30 })}
        />

        <TransitionSeries.Sequence durationInFrames={150}>
          <FullscreenImage
            imageSrc={img02src}
          />
        </TransitionSeries.Sequence>

        <TransitionSeries.Transition
          presentation={pullOut()}
          timing={linearTiming({ durationInFrames: 30 })}
        />

        <TransitionSeries.Sequence durationInFrames={150}>
          <FullscreenImage imageSrc={img03src} />
        </TransitionSeries.Sequence>
      </TransitionSeries>
      <SlidingReveal
        startFrame={180}
        endFrame={465}
        inAnimation={"slideRight"}
        outAnimation={"slideLeft"}
        titleText={revealText}
        revealHeight={60}
        revealStartFrame={25}
        revealDuration={25}
        fontSize={48}
        fontWeight="700"
      />
      <ParagraphDisplay
        startFrame={214}
        inAnimation={"slideBottom"}
        outAnimation={"slideBottom"}
        paragraphText={paragraphText}
        endFrame={465}
      />
    </AbsoluteFill>
  );
};

// Calculate metadata function (optional, similar to Hair)
export const calculateLandingPageMetadata = async ({ props }) => {
  const { videoDuration = 540 } = props;

  console.log(`🎬 Landing Page video duration: ${videoDuration} frames (${Math.round(videoDuration / 30)}s)`);

  return {
    durationInFrames: Math.max(540, videoDuration),
  };
};

export default LandingPageComposition;

;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
